/**
 * Back office → Calls (C16, D-151): the journal of the WhatsApp calls received on the agency's
 * number (missed ones to call back first), the « calls enabled » setting (sent to Meta in live
 * mode) and, in test mode, a button that simulates an incoming call without Meta.
 */

import "./calls.css";

import {
  type CallsListParams,
  useCallsList,
  useCallsSettingsPartialUpdate,
  useCallsSettingsRetrieve,
  useCallsSimulateCreate,
  useCallsSummaryRetrieve,
} from "@agencyos/api-client";
import { Button, EmptyState, GlassCard, SegmentedControl, SelectField, Skeleton, SoftToggle, StatCard, TextField, useToast } from "@agencyos/ui-web";
import { PhoneIncoming, PhoneMissed, PhoneCall, PhoneForwarded } from "lucide-react";
import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";

import { Page } from "../../components/AppShell";
import { fieldErrors, useCurrentRole } from "../../lib/settings";
import { Pager } from "../billing/shared";
import { CallRow, useRefreshCalls } from "./CallList";

const VIEWS = ["pending", "missed", "answered", "all"] as const;
type View = (typeof VIEWS)[number];

function paramsOf(view: View, page: number): CallsListParams {
  const params: CallsListParams = { page };
  if (view === "pending") params.pending = true;
  if (view === "missed" || view === "answered") params.status = view;
  return params;
}

function Summary() {
  const { t } = useTranslation();
  const summary = useCallsSummaryRetrieve();
  const data = summary.data?.status === 200 ? summary.data.data : undefined;
  const value = (n?: number) => (n === undefined ? <Skeleton style={{ width: 36, height: 26 }} /> : n);
  return (
    <section className="kpis calls-kpis" aria-label={t("calls.title")}>
      <StatCard tone="accent" icon={<PhoneCall size={18} strokeWidth={1.75} />} label={t("calls.summary.today")} value={value(data?.today)} />
      <StatCard tone="success" icon={<PhoneIncoming size={18} strokeWidth={1.75} />} label={t("calls.summary.answered")} value={value(data?.answered_today)} />
      <StatCard tone="danger" icon={<PhoneMissed size={18} strokeWidth={1.75} />} label={t("calls.summary.missed")} value={value(data?.missed_today)} />
      <StatCard tone="warning" icon={<PhoneForwarded size={18} strokeWidth={1.75} />} label={t("calls.summary.pending")} value={value(data?.missed_pending)} />
    </section>
  );
}

function SettingsCard({ canEdit }: { canEdit: boolean }) {
  const { t } = useTranslation();
  const toast = useToast();
  const refresh = useRefreshCalls();
  const settings = useCallsSettingsRetrieve();
  const save = useCallsSettingsPartialUpdate({
    mutation: {
      onSuccess: () => {
        refresh();
        toast.show(t("calls.settings.saved"), { tone: "success" });
      },
    },
  });
  const row = settings.data?.status === 200 ? settings.data.data : undefined;
  if (!row) return settings.isPending ? <Skeleton style={{ height: 120 }} /> : null;
  return (
    <GlassCard as="section" className="stack" aria-label={t("calls.settings.title")}>
      <h2>{t("calls.settings.title")}</h2>
      <SoftToggle
        label={t("calls.settings.enabled")}
        checked={Boolean(row.enabled)}
        disabled={!canEdit || save.isPending}
        onChange={(enabled) => save.mutate({ data: { enabled } })}
      />
      <p className="muted">{t("calls.settings.enabledHint")}</p>
      <SoftToggle
        label={t("calls.settings.notifyMissed")}
        checked={Boolean(row.notify_missed)}
        disabled={!canEdit || save.isPending}
        onChange={(notify_missed) => save.mutate({ data: { notify_missed } })}
      />
      <p className="muted">{t("calls.settings.notifyMissedHint")}</p>
      <p className="muted">{row.live ? t("calls.settings.live") : t("calls.settings.sandbox")}</p>
      {row.meta_error ? <p role="alert">{t("calls.settings.metaError", { error: row.meta_error })}</p> : null}
      <p className="muted">{t("calls.settings.availability")}</p>
    </GlassCard>
  );
}

function SimulateCard() {
  const { t } = useTranslation();
  const toast = useToast();
  const refresh = useRefreshCalls();
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [outcome, setOutcome] = useState<"missed" | "answered">("missed");
  const simulate = useCallsSimulateCreate({
    mutation: {
      onSuccess: () => {
        refresh();
        toast.show(t("calls.simulate.done"), { tone: "success" });
      },
    },
  });
  const errors = fieldErrors(simulate.error);
  const submit = (event: FormEvent) => {
    event.preventDefault();
    simulate.mutate({ data: { phone, name, outcome, duration: outcome === "answered" ? 60 : 0 } });
  };
  return (
    <GlassCard as="section" className="stack" aria-label={t("calls.simulate.title")}>
      <h2>{t("calls.simulate.title")}</h2>
      <p className="muted">{t("calls.simulate.hint")}</p>
      <form className="stack" onSubmit={submit}>
        <TextField label={t("calls.simulate.phone")} value={phone} onChange={(e) => setPhone(e.target.value)} error={errors.phone} inputMode="tel" required placeholder="+237 6 79 20 58 65" />
        <TextField label={t("calls.simulate.name")} value={name} onChange={(e) => setName(e.target.value)} />
        <SelectField
          label={t("calls.simulate.outcome")}
          value={outcome}
          onChange={(e) => setOutcome(e.target.value === "answered" ? "answered" : "missed")}
          options={[
            { value: "missed", label: t("calls.simulate.missed") },
            { value: "answered", label: t("calls.simulate.answered") },
          ]}
        />
        {errors.non_field_errors || errors[""] ? <p role="alert">{errors.non_field_errors ?? errors[""]}</p> : null}
        <Button type="submit" loading={simulate.isPending}>
          {t("calls.simulate.submit")}
        </Button>
      </form>
    </GlassCard>
  );
}

export function CallsPage() {
  const { t } = useTranslation();
  const role = useCurrentRole("calls");
  const isAdmin = role === "owner" || role === "manager";
  const [view, setView] = useState<View>("pending");
  const [page, setPage] = useState(1);
  const calls = useCallsList(paramsOf(view, page));
  const settings = useCallsSettingsRetrieve();
  const live = settings.data?.status === 200 ? Boolean(settings.data.data.live) : true;
  const data = calls.data?.status === 200 ? calls.data.data : undefined;
  return (
    <Page title={t("calls.title")} description={t("calls.intro")}>
      <Summary />
      <div className="calls-layout">
        <GlassCard as="section" className="stack calls-journal" aria-label={t("calls.title")}>
          <SegmentedControl
            label={t("calls.title")}
            value={view}
            onChange={(next) => {
              setView(next);
              setPage(1);
            }}
            options={VIEWS.map((v) => ({ value: v, label: t(`calls.views.${v}`) }))}
          />
          {calls.isPending ? <Skeleton style={{ height: 120 }} /> : null}
          {data && !data.results.length ? (
            <EmptyState icon={<PhoneIncoming size={22} strokeWidth={1.75} />} title={t("calls.empty")} description={t("calls.emptyHint")} />
          ) : null}
          {data?.results.length ? (
            <ul className="plain-list call-list">
              {data.results.map((call) => (
                <CallRow key={call.id} call={call} showContact />
              ))}
            </ul>
          ) : null}
          {data ? (
            <Pager
              page={page}
              hasNext={Boolean(data.next)}
              onChange={setPage}
              labels={{ previous: t("notifications.log.previous"), next: t("notifications.log.next"), page: t("notifications.log.page", { page }) }}
            />
          ) : null}
        </GlassCard>
        <div className="stack">
          <SettingsCard canEdit={isAdmin} />
          {isAdmin && !live ? <SimulateCard /> : null}
        </div>
      </div>
    </Page>
  );
}
